import {LfI18n, LfStorage} from '@lightweightform/core';
import {
  I18N_SELECTABLE_NO_OPTIONS_TEXT_KEY,
  I18N_SELECTABLE_OPTIONS_KEY,
  I18N_TABLE_COLUMN_LABELS_KEY,
  I18N_VALUE_CODE_KEY,
  I18N_VALUE_HELP_MESSAGE_KEY,
  I18N_VALUE_LABEL_KEY
} from 'lf-at-theme';
import {
  allCountriesCodeOptions,
  euCountriesExceptPtCodeOptions,
  nonEuCountriesCodeOptions,
  onlyPtCountryCodeOptions,
  peculiarCountriesCodeOptions,
  memberStatesExceptPtCodeOptions,
  memberStatesCodeOptions,
  allCountriesCodeOptionsWithoutArtificialCodes
} from '../../catalog/countries.i18n.en-US';
import {ERRO_REGISTO} from '../../catalog/server/serverCatalogs';
import {getKtData} from './helpers';

//Na versão 7 deixará de ser possivel de utilizar codigos ficticios na morada
//Por isso apenas apareceram esses codigos na lista quando se está a consulta um registo (ou seja no modo de detalhe)
function peculiarAddressCountriesByFieldFn(parentCountryPath) {
  return (ctx: LfStorage) => {
    const initialValues = ctx.getStateProperty('/', 'initialValues') || {};
    const filterArtificialCountryCodes = !initialValues.mode.detail;

    return peculiarCountriesCodeOptions(ctx.get(parentCountryPath),filterArtificialCountryCodes)
  }
}

function peculiarCountriesByFieldFn(parentCountryPath) {
  return ctx => peculiarCountriesCodeOptions(ctx.get(parentCountryPath),false)
}

const countriesOfBusinessOptions = function ({defaultNonResident}: { defaultNonResident: boolean }) {
  return (ctx: LfStorage) => {
    const initialValues = ctx.getStateProperty('/', 'initialValues') || {};
    const isNonResident = initialValues.isNonResident ?? defaultNonResident;
    const isByIntermediary = initialValues.registeredBy == 'INTERMEDIARY';
    const isDetail = !!initialValues.mode.detail;
    return isDetail
      ? allCountriesCodeOptions
      : isByIntermediary
        ? allCountriesCodeOptions
        : isNonResident
          ? [...nonEuCountriesCodeOptions, ...euCountriesExceptPtCodeOptions]
          : onlyPtCountryCodeOptions;
  };
};
const contactCountriesOptions = allCountriesCodeOptionsWithoutArtificialCodes;
const fixedEstabIssuingCountries = memberStatesExceptPtCodeOptions;
const fixedEstabAddressCountries = allCountriesCodeOptionsWithoutArtificialCodes;
const idsInOtherMSsCountries = memberStatesExceptPtCodeOptions;
const prevOrCurrOssVatIdCountries = memberStatesCodeOptions;

const prevOrCurrOssVatIdSchemes = [
  {value: 'UNION', label: 'Union'},
  {value: 'NON_UNION', label: 'non-Union'},
  {value: 'IMPORT', label: 'Import'},
];

export const yesNoOptions = [
  {value: true, code: '', label: 'Yes'},
  {value: false, code: '', label: 'No'},
];

export const addressTypeOptions = [
  {value: 'structured', code: '', label: 'Structured'},
  {value: 'freeText', code: '', label: 'Free Text'},
];

export const contactNameTypeOptions = [
  {value: 'structured', code: '', label: 'Structured'},
  {value: 'freeText', code: '', label: 'Free Text'},
];

export const taxVatIdColumnNames = {
  scheme: 'Scheme',
  issuingCountry: 'Country',
  vatIdNum: 'VAT Number',
};

export const sharedFormI18nEnUS: (allowsResidents: boolean) => Record<string, any> = (allowsResidents) => (LfI18n.mergeTranslations(
  {},
  ERRO_REGISTO.en_US,
  {
    '*': {
      validations: {
        ONLY_SINGLE_SPACES_ALLOWED: 'There must be no consecutive spaces in this field.',

        VAT_ID_ONE_TYPE_REQUIRED: 'The VAT number must be filled.',
        VAT_ID_ONLY_ONE_ALLOWED: 'Only one of [VAT Number](lf://./vatIdNum) or [Tax Number](lf://./taxRefNum) may be filled.',
        VAT_ID_PARTIALLY_FILLED: 'Either a [VAT Number](lf://./vatIdNum) or a [Tax Number](lf://./taxRefNum) should be filled. An [Issuing Country](lf://./issuingCountry) should also be filled.',

        VAT_ID_VAT_TYPE_NOT_ALLOWED: 'This type of identification number is not allowed.',
        VAT_ID_TAX_TYPE_NOT_ALLOWED: 'This type of identification number is not allowed.',
        VAT_ID_SCHEME_NOT_ALLOWED: 'This scheme is not allowed.',

        VAT_ID_INVALID_VAT_FORMAT: 'VAT Identification numbers must have 1 to 12 of the following characters: upper case Latin letters, \'+\', \'*\', or digits.',
        VAT_ID_INVALID_VOES_FORMAT: (data) => `VOES numbers must be of the format EUnnnaaaaaa, where nnn is the issuing country\'s numeric code (${getKtData(data, 'n3Code')}) and aaaaaa is the actual identification.`,
        VAT_ID_INVALID_IOSS_FORMAT: (data) => `IOSS numbers must be of the format IMnnnaaaaaaa, where nnn is the issuing country\'s numeric code (${getKtData(data, 'n3Code')}) and aaaaaaa is the actual identification.`,
        VAT_ID_INVALID_INT_FORMAT: (data) => `INT numbers must be of the format INnnnaaaaaaa, where nnn is the issuing country\'s numeric code (${getKtData(data, 'n3Code')}) and aaaaaaa is the actual identification.`,
        VAT_ID_TAX_REF_NUM_INVALID_FORMAT: 'Tax Reference numbers must have 1 to 50 characters, no leading or trailing spaces, and no multiple space gaps.',

        ADDRESS_COUNTRY_INVALID: 'Invalid [address country](lf://./address/country).',
        ADDRESS_STRUCTURED_FIELDS_BUT_NOT_STRUCTURED: 'Structured address field is filled but the [address type](lf://./address/type) is not structured.',

        CONTACT_NAME_STRUCTURED_FIELDS_BUT_NOT_STRUCTURED: 'Structured contact field is filled but the [contact type](lf://./contactName/type) is not structured.',

        BANK_ACCOUNT_INFO_NOT_ALLOWED: 'Bank account info not allowed.',

        ACCOUNT_HOLDER_INVALID: 'The provided account holder contains invalid characters. The allowed characters include alphanumeric characters (without accentuation), \'?\', \'/\', \'\ \' \, \'-\', \':\', \'(\', \')\', \'.\', \',\', \'\'\', \'+\', \'.\'. Additionally, only alphanumeric characters are allowed at the beginning of the account holder.' ,

        COMMENCEMENT_DATE_NOT_ALLOWED: 'Supply commencement date is not allowed.',
        COMMENCEMENT_DATE_BEFORE_OSS_START: 'Supply commencement date should not be before ECOM-OSS start date.',

        HAVE_TRADING_NAMES_BUT_NO_COMPANY_NAME: 'Please provide [Company name](lf://./companyName) before providing any extra trading names.',

        FIXED_ESTABLISHMENT_NOT_REAL: 'Only fixed establishments in Member States with an actual establishment are allowed. You must fill-in either a VAT Identification Number or a Tax Reference Number.',
        FIXED_ESTABLISHMENT_NOT_STOCK: 'When not established in the Union, only consignment stocks can be filled-in as fixed establishments. You must leave both VAT Identification Number and Tax Reference Number empty.',
      }
    },

    '/?': {
      identification: 'Identification',
      contact: 'Contact',
      loadedPrevRegistration: 'We pre-loaded the information from your previous registration. Please review it before submitting.',
    },

    '/?/currVatId/issuingCountry': {
      [I18N_VALUE_CODE_KEY]: '1',
    },

    '/?/companyName': {
      label: 'Company name',
      [I18N_VALUE_CODE_KEY]: '3'
    },

    '/?/tradingNames': {
      label: 'Trading name(s), if different from above.',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Trading name(s) of the company if different from the company name',
      [I18N_TABLE_COLUMN_LABELS_KEY]: {name: 'Trading name'},
      [I18N_VALUE_CODE_KEY]: '4'
    },

    '/?/tradingNames/?/name': {
      [I18N_VALUE_LABEL_KEY]: 'Trading name',
      [I18N_VALUE_CODE_KEY]: '4'
    },

    '/?/countryOfBusiness': {
      label: 'Country in which you have your business, if not in the EU',
      [I18N_SELECTABLE_OPTIONS_KEY]: countriesOfBusinessOptions({defaultNonResident: !allowsResidents}), // If we allow residents, if nothing is said, we assume we're dealing with a resident.
      [I18N_VALUE_CODE_KEY]: '6',
      validations: {
        COUNTRY_OF_BUSINESS_INVALID: 'Invalid [country of business](lf://.).',
        COUNTRY_OF_BUSINESS_EU_NO_REGION: 'A Member State can only be [country of business](lf://.) if a [region/territory](lf://../cobRegion) is filled-in that is both outside the Union and from that Member State.',
        COUNTRY_OF_BUSINESS_GB_NO_REGION: 'When selecting United Kingdom as [country of business](lf://.) a [region/territory](lf://../cobRegion) must also be selected.',
      },
    },
    '/?/cobRegion': {
      label: 'Region/Territory',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Territories or regions with privileged taxation',
      [I18N_SELECTABLE_OPTIONS_KEY]: peculiarCountriesByFieldFn('../countryOfBusiness'),
      [I18N_SELECTABLE_NO_OPTIONS_TEXT_KEY]: 'No options',
      validations: {
        REGION_WRONG_COUNTRY_OF_BUSINESS: 'The region does not belong to this [country](lf://../countryOfBusiness)',
        REGION_NOT_OUTSIDE_UNION: 'The region is not outside de Union',
      },
    },

    /* Address */
    '/?/address': {
      label: 'Address',
      validations: {
        ADDRESS_UK_NO_REGION: 'When selecting United Kingdom as [country](lf://./country), one of its [regions](lf://./region) must also be selected.',
      }
    },
    '/?/address/type': {
      label: 'Address Type',
      [I18N_SELECTABLE_OPTIONS_KEY]: addressTypeOptions,
      [I18N_VALUE_CODE_KEY]: '5'
    },
    /* Structured */
    '/?/address/street': {label: 'Street'},
    '/?/address/buildingNumber': {label: 'Number'},
    '/?/address/floorNumber': {label: 'Floor'},
    '/?/address/postCode': {
      label: 'Postal Code',
      validations: {
        ADDRESS_POST_CODE_INVALID_FORMAT: 'The postal code must be filled with format NNNN-NNN.'
      }
    },
    '/?/address/city': {label: 'City'},
    '/?/address/country': {label: 'Country', [I18N_SELECTABLE_OPTIONS_KEY]: contactCountriesOptions},
    '/?/address/region': {
      label: 'Region/Territory',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Territories or regions with privileged taxation',
      [I18N_SELECTABLE_OPTIONS_KEY]: peculiarAddressCountriesByFieldFn('../country'),
      [I18N_SELECTABLE_NO_OPTIONS_TEXT_KEY]: 'No options',
      validations: {
        ADDRESS_REGION_WRONG_COUNTRY: 'The region does not belong to this [country](lf://../country).',
        ADDRESS_WITH_ARTIFICIAL_CODE_NOT_ALLOWED: 'The [region](lf://../region) is invalid.',
      },
    },
    /* Structured - Extra address fields */
    '/?/address/suite': {label: 'Suite'},
    '/?/address/district': {label: 'District'},
    '/?/address/postOfficeBox': {label: 'Post Office Box'},
    '/?/address/countrySubEntity': {label: 'Country Sub-Entity'},
    '/?/address/otherLocalId': {label: 'Other Local ID'},
    /* Free Text */
    '/?/address/freeText': {label: 'Free Text'},

    '/?/websites': {
      label: 'Website(s) of the taxable person when available',
      [I18N_TABLE_COLUMN_LABELS_KEY]: {name: 'Website'},
      [I18N_VALUE_CODE_KEY]: '8'
    },
    '/?/websites/?': {
      validations: {
        WEBSITE_INVALID: 'Invalid website URL.',
        WEBSITE_NOT_GIVEN: 'At aleast one website URL is required.',
      }
    },
    '/?/websites/?/name': {label: 'Name'},

    /* Contact Name */
    '/?/contactName': {
      label: 'Contact person',
    },
    '/?/contactName/type': {
      label: 'Name Type',
      [I18N_SELECTABLE_OPTIONS_KEY]: contactNameTypeOptions,
      [I18N_VALUE_CODE_KEY]: '9'
    },
    '/?/contactName/precedingTitle': {label: 'Preceding Title'},
    '/?/contactName/firstName': {label: 'First Name'},
    '/?/contactName/lastNamePrefix': {label: 'Last Name Prefix'},
    '/?/contactName/lastName': {label: 'Last Name'},
    '/?/contactName/generalSuffix': {label: 'General Suffix'},
    '/?/contactName/maidenName': {label: 'Maiden Name'},
    /* Contact Name - Repeatable parts */
    '/?/contactName/titles': {label: 'Titles'},
    '/?/contactName/middleNames': {label: 'Middle Names'},
    '/?/contactName/generationIdentifiers': {label: 'Generation Identifiers'},
    '/?/contactName/suffixes': {label: 'Suffixes'},
    '/?/contactName/titles/?/name': {label: 'Name'},
    '/?/contactName/middleNames/?/name': {label: 'Name'},
    '/?/contactName/generationIdentifiers/?/name': {label: 'Name'},
    '/?/contactName/suffixes/?/name': {label: 'Name'},
    /* Free text name */
    '/?/contactName/freeText': {label: 'Free Text'},
    /* More Contact Info */
    '/?/contactInfo': {
      label: 'Contact information',
    },
    '/?/contactInfo/email': {
      label: 'Email address of the taxable person',
      [I18N_VALUE_CODE_KEY]: '7',
      validations: {EMAIL_INVALID: 'Invalid email address format.'}
    },
    '/?/contactInfo/emailConfirmation': {
      label: 'Confirm Email',
      validations: {EMAIL_CONFIRMATION_NOT_EQUAL: 'The email addresses are not equal.'}
    },
    '/?/contactInfo/telephone': {
      label: 'Telephone number',
      [I18N_VALUE_CODE_KEY]: '10',
      validations: {
        TELEPHONE_INVALID: 'Invalid phone number.'
      }
    },

    // Bank account information
    '/?/bankAccountInfo': {
      label: 'Bank Account Information',
      validations: {
        BANK_ACCOUNT_ID_NOT_PROVIDED: 'The field is required.',
      }
    },
    '/?/bankAccountInfo/iban': {
      label: 'IBAN number ',
      [I18N_VALUE_CODE_KEY]: '11',
      validations: {
        IBAN_INVALID: 'Invalid IBAN number'
      },
    },
    '/?/bankAccountInfo/bic': {
      label: 'BIC number',
      [I18N_VALUE_CODE_KEY]: '12',
      validations: {
        BIC_INVALID: 'Invalid BIC number'
      },
    },
    '/?/bankAccountInfo/accountHolder': {label: 'Account Holder'},

    // Fixed establishments
    '/?/fixedEstablishments': {
      label: 'Fixed establishments/Expedition places in other EU countries',
      validations: {
        FIXED_ESTABLISHMENT_DUPLICATED: 'There cannot be more than one Fixed Establishment with the same VAT.'
      },
    },
    '/?/fixedEstablishments/?': {
      fixedEstablishmentsVatId: 'Identification',
      address: 'Address',

      label: (ctx: LfStorage) => {
        const val = ctx.get('./taxIdentif') as { issuingCountry: string, vatIdNum: string, taxRefNum: string };
        return val.issuingCountry + '/' + (val.vatIdNum || val.taxRefNum);
      },
    },
    '/?/fixedEstablishments/?/taxIdentif': {
      label: '13.1'
    },
    '/?/fixedEstablishments/?/taxIdentif/issuingCountry': {
      label: 'Issuing Country',
      [I18N_VALUE_CODE_KEY]: '13.1',
      [I18N_SELECTABLE_OPTIONS_KEY]: fixedEstabIssuingCountries
    },
    '/?/fixedEstablishments/?/taxIdentif/vatIdNum': {
      label: 'VAT Identification Number'
    },
    '/?/fixedEstablishments/?/taxIdentif/taxRefNum': {
      label: 'Tax Reference Number'
    },
    '/?/fixedEstablishments/?/tradingName': {
      label: 'Trading Name',
      [I18N_VALUE_CODE_KEY]: '14.1',
    },
    '/?/fixedEstablishments/?/address': {
      label: 'Address',
      validations: {
        ADDRESS_UK_NO_REGION: 'When selecting United Kingdom as [country](lf://./country), one of its [regions](lf://./region) must also be selected.',
      }
    },
    '/?/fixedEstablishments/?/address/type': {
      label: 'Address Type',
      [I18N_VALUE_CODE_KEY]: '14.1',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Full postal address and trading name of fixed establishments and places where goods are dispatched or transported from in Member States other than the Member State of identification',
      [I18N_SELECTABLE_OPTIONS_KEY]: addressTypeOptions
    },
    '/?/fixedEstablishments/?/address/street': {label: 'Street'},
    '/?/fixedEstablishments/?/address/buildingNumber': {label: 'Number'},
    '/?/fixedEstablishments/?/address/floorNumber': {label: 'Floor'},
    '/?/fixedEstablishments/?/address/postCode': {
      label: 'Postal Code',
      validations: {
        ADDRESS_POST_CODE_INVALID_FORMAT: 'The postal code must be filled with format NNNN-NNN.'
      }
    },
    '/?/fixedEstablishments/?/address/city': {label: 'City'},
    '/?/fixedEstablishments/?/address/country': {
      label: 'Country',
      [I18N_SELECTABLE_OPTIONS_KEY]: fixedEstabAddressCountries
    },
    '/?/fixedEstablishments/?/address/region': {
      label: 'Region/Territory',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Territories or regions with privileged taxation',
      [I18N_SELECTABLE_OPTIONS_KEY]: peculiarAddressCountriesByFieldFn('../country'),
      [I18N_SELECTABLE_NO_OPTIONS_TEXT_KEY]: 'No options',
      validations: {
        ADDRESS_REGION_WRONG_COUNTRY: 'The region does not belong to this [country](lf://../country).',
        ADDRESS_WITH_ARTIFICIAL_CODE_NOT_ALLOWED: 'The [region](lf://../region) is invalid.',
      },
    },
    '/?/fixedEstablishments/?/address/suite': {label: 'Suite'},
    '/?/fixedEstablishments/?/address/postOfficeBox': {label: 'Post Office Box'},
    '/?/fixedEstablishments/?/address/district': {label: 'District'},
    '/?/fixedEstablishments/?/address/countrySubEntity': {label: 'Country Sub-Entity'},
    '/?/fixedEstablishments/?/address/otherLocalId': {label: 'Other Local ID'},
    '/?/fixedEstablishments/?/address/freeText': {label: 'Free Text'},

    // Identification in other EU countries / Member States
    '/?/idsInOtherMSs': {
      [I18N_TABLE_COLUMN_LABELS_KEY]: taxVatIdColumnNames
    },
    '/?/idsInOtherMSs/?/issuingCountry': {
      label: 'Country',
      [I18N_SELECTABLE_OPTIONS_KEY]: idsInOtherMSsCountries
    },
    '/?/idsInOtherMSs/?/vatIdNum': {label: 'VAT Number'},
    // Previous registrations for special schemes
    '/?/prevOrCurrOssVatId': {
      label: 'Previous/Current VAT Id. Numbers',
      headerText: 'Registration under special schemes in other Member States',
      subHeaderText: 'Effective from 2021/07/01',
      [I18N_VALUE_CODE_KEY]: '21',
      [I18N_TABLE_COLUMN_LABELS_KEY]: taxVatIdColumnNames
    },
    '/?/prevOrCurrOssVatId/?/scheme': {
      label: 'Scheme',
      [I18N_SELECTABLE_OPTIONS_KEY]: prevOrCurrOssVatIdSchemes
    },
    '/?/prevOrCurrOssVatId/?/issuingCountry': {
      label: 'Country',
      [I18N_SELECTABLE_OPTIONS_KEY]: prevOrCurrOssVatIdCountries
    },
    '/?/prevOrCurrOssVatId/?/vatIdNum': {label: 'VAT Number'},

    // Supply Commencement
    '/?/supplyCommencement': {
      label: 'Supply Commencement',
    },
    '/?/supplyCommencement/alreadyCarriedOutSupplies': {
      label: 'Have you already carried out supplies under the special scheme? If yes, please indicate the first date of supply below.',
      [I18N_SELECTABLE_OPTIONS_KEY]: yesNoOptions
    },
    '/?/supplyCommencement/commencementDate': {
      label: 'Date of commencement of using the scheme',
      [I18N_VALUE_CODE_KEY]: '17'
    },

    // Relevant Dates
    '/?/relevantDates': {
      label: 'Relevant Dates'
    },
    '/?/relevantDates/registrationReqDate': {
      label: 'Request date',
      [I18N_VALUE_CODE_KEY]: '18',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Date of request by the taxable person to be registered under the scheme',
    },
    '/?/relevantDates/forecastStartDate': {
      label: 'Commencement date',
      [I18N_VALUE_CODE_KEY]: '17',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Date of commencement of using the scheme',
    },
    '/?/relevantDates/msidDecisionDate': {
      label: 'Registration date',
      [I18N_VALUE_CODE_KEY]: '19',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Date of registration decision by the Member State of identification',
    },

    // Consent / Change of MSID
    '/?/consents': {
      label: 'Notices',
      changeMSIDNotice: 'If you already used this scheme in another EU country and you have closed or will close your fixed establishment in that country, and you want to continue to use the scheme by registering with us, please indicate the VAT identification number that you registered with in the other EU country. Please note that in order for us to accept your application, you must deregister with the other EU country before the end of the tenth day following the end of this month.',
      changeMSIDHeader: 'Change of identification country',
    },

  }));
